import * as signalR from '@microsoft/signalr';
import { EntitiesApiUrl } from './ExternalApi';
import { ChatMessageDto } from './ChatMessagesApi';

export type SignalRMessageCallback = (message: ChatMessageDto, affectedParticipants?: Array<{ userId: string; newUnreadCount: number }>) => void;
export type SignalRTypingCallback = (data: { conversationId: string; userId?: string; displayName?: string; isTyping: boolean; from?: string }) => void;
export type SignalRReadStatusCallback = (data: { conversationId: string; userId: string; role?: string; displayName?: string; lastReadAt?: string; lastReadMessageId?: string; unreadCount: number }) => void;
export type SignalRNewConversationCallback = (data: { conversationId: string; bookingCustomerId?: string; bookingGuid?: string; customerName?: string; lastMessage?: any; websiteId: string; createdAt: string }) => void;
/** Outcome of forwarding one of our messages to an external platform (Booking.com today). */
export type SignalRMessageDeliveryCallback = (data: { conversationId: string; messageId: string; channel: string; deliveryStatus: string }) => void;
/** Messages were recovered from an external platform's history — the thread should refetch. */
export type SignalRHistoryBackfilledCallback = (data: { conversationId: string; recovered: number }) => void;
export type SignalRConversationUpdatedCallback = (data: { conversationId: string; status: 'open' | 'resolved'; resolvedAt?: string | null; websiteId: string; assignedToUserId?: string | null; assignedAgentName?: string | null }) => void;

/**
 * SignalR connection manager for real-time chat updates.
 * Connects to the /hubs/chat endpoint and handles automatic reconnection.
 * Includes health monitoring to detect and recover from connection issues.
 */
export class ChatSignalRConnection {
  private connection: signalR.HubConnection | null = null;
  private isConnecting = false;
  private isConnected = false;
  private healthCheckInterval: number | null = null;
  private lastHealthCheckTime: number = 0;
  private connectionStateListeners: Array<(connected: boolean) => void> = [];
  // Shared in-flight start promise so concurrent callers await the same connection
  private startPromise: Promise<void> | null = null;
  // Set while an intentional stop() is in progress so onclose doesn't auto-reconnect
  private intentionalStop = false;

  /**
   * Start the SignalR connection.
   * Safe to call concurrently: callers share the same in-flight connection promise,
   * so a second caller awaits the real connection instead of returning early and
   * then failing its group-join with "Not connected".
   */
  async start(): Promise<void> {
    if (this.isConnected) {
      return;
    }
    if (this.startPromise) {
      return this.startPromise;
    }

    this.startPromise = this.doStart();
    try {
      await this.startPromise;
    } finally {
      this.startPromise = null;
    }
  }

  private async doStart(): Promise<void> {
    this.isConnecting = true;
    this.intentionalStop = false;

    try {
      this.connection = new signalR.HubConnectionBuilder()
        .withUrl(`${EntitiesApiUrl}/hubs/chat`)
        .withAutomaticReconnect()
        .build();

      // Setup reconnection handlers
      this.connection.onreconnecting((error) => {
        console.warn('ChatSignalR: Reconnecting...', error);
        this.isConnected = false;
        this.notifyConnectionStateChange(false);
      });

      this.connection.onreconnected((connectionId) => {
        console.log('ChatSignalR: Reconnected', connectionId);
        this.isConnected = true;
        this.lastHealthCheckTime = Date.now();
        this.notifyConnectionStateChange(true);
      });

      this.connection.onclose((error) => {
        console.warn('ChatSignalR: Connection closed', error);
        this.isConnected = false;
        this.isConnecting = false;
        this.notifyConnectionStateChange(false);

        // Don't auto-reconnect if the close was triggered by an intentional stop()
        if (this.intentionalStop) {
          console.log('ChatSignalR: Connection closed intentionally - not reconnecting');
          return;
        }

        // Attempt to reconnect after 5 seconds
        console.log('ChatSignalR: Scheduling reconnection in 5 seconds...');
        setTimeout(() => {
          if (!this.isConnected && !this.isConnecting && !this.intentionalStop) {
            console.log('ChatSignalR: Attempting to reconnect after connection closed');
            this.start().catch(err => {
              console.error('ChatSignalR: Failed to reconnect after close', err);
            });
          }
        }, 5000);
      });

      await this.connection.start();
      this.isConnected = true;
      this.isConnecting = false;
      this.lastHealthCheckTime = Date.now();
      console.log('ChatSignalR: Connected successfully');
      this.notifyConnectionStateChange(true);
      
      // Start health monitoring
      this.startHealthMonitoring();
    } catch (error) {
      console.error('ChatSignalR: Failed to start connection', error);
      this.isConnecting = false;
      this.isConnected = false;
      this.notifyConnectionStateChange(false);
      throw error;
    }
  }

  /**
   * Stop the SignalR connection
   */
  async stop(): Promise<void> {
    // Mark as intentional so the onclose handler doesn't schedule a reconnect
    this.intentionalStop = true;

    // Stop health monitoring
    this.stopHealthMonitoring();

    if (this.connection) {
      await this.connection.stop();
      this.connection = null;
      this.isConnected = false;
      this.isConnecting = false;
      console.log('ChatSignalR: Connection stopped');
      this.notifyConnectionStateChange(false);
    }
  }

  /**
   * Start health monitoring (checks connection every 60 seconds)
   */
  private startHealthMonitoring(): void {
    // Clear any existing interval
    this.stopHealthMonitoring();
    
    console.log('ChatSignalR: Starting health monitoring (60-second interval)');
    
    this.healthCheckInterval = window.setInterval(async () => {
      await this.performHealthCheck();
    }, 60000); // 60 seconds
  }

  /**
   * Stop health monitoring
   */
  private stopHealthMonitoring(): void {
    if (this.healthCheckInterval !== null) {
      window.clearInterval(this.healthCheckInterval);
      this.healthCheckInterval = null;
      console.log('ChatSignalR: Stopped health monitoring');
    }
  }

  /**
   * Perform a health check on the connection
   */
  private async performHealthCheck(): Promise<void> {
    const now = Date.now();
    const timeSinceLastCheck = now - this.lastHealthCheckTime;
    
    console.log(`[ChatSignalR Health Check] Running... (${Math.round(timeSinceLastCheck / 1000)}s since last check)`);
    
    // Check if connection exists and state is correct
    if (!this.connection) {
      console.warn('[ChatSignalR Health Check] ❌ No connection object - attempting to reconnect');
      await this.attemptReconnection();
      return;
    }
    
    const state = this.connection.state;
    console.log(`[ChatSignalR Health Check] Connection state: ${signalR.HubConnectionState[state]}`);
    
    // Check if state matches our internal flag
    if (state === signalR.HubConnectionState.Connected) {
      if (!this.isConnected) {
        console.warn('[ChatSignalR Health Check] ⚠️ State mismatch: connected but flag is false - fixing');
        this.isConnected = true;
        this.notifyConnectionStateChange(true);
      }
      
      // Try a ping to verify the connection is really working
      try {
        // Invoke a simple server method to verify connectivity
        // If your hub has a Ping method, use it. Otherwise, we'll check the state
        console.log('[ChatSignalR Health Check] ✓ Connection healthy');
        this.lastHealthCheckTime = now;
      } catch (error) {
        console.error('[ChatSignalR Health Check] ❌ Ping failed despite Connected state', error);
        await this.attemptReconnection();
      }
    } else if (state === signalR.HubConnectionState.Disconnected) {
      console.warn('[ChatSignalR Health Check] ❌ Disconnected - attempting to reconnect');
      await this.attemptReconnection();
    } else if (state === signalR.HubConnectionState.Reconnecting) {
      console.log('[ChatSignalR Health Check] ⏳ Currently reconnecting - waiting...');
      // Let the automatic reconnection handle it
    } else {
      console.warn(`[ChatSignalR Health Check] ⚠️ Unexpected state: ${signalR.HubConnectionState[state]}`);
    }
  }

  /**
   * Attempt to reconnect the SignalR connection
   */
  private async attemptReconnection(): Promise<void> {
    if (this.isConnecting) {
      console.log('[ChatSignalR Reconnection] Already attempting to connect - skipping');
      return;
    }
    
    console.log('[ChatSignalR Reconnection] Starting reconnection attempt...');
    
    try {
      // Stop existing connection if any
      if (this.connection) {
        try {
          await this.connection.stop();
        } catch (err) {
          console.warn('[ChatSignalR Reconnection] Error stopping old connection:', err);
        }
        this.connection = null;
      }
      
      this.isConnected = false;
      this.isConnecting = false;
      
      // Start fresh connection
      await this.start();
      console.log('[ChatSignalR Reconnection] ✓ Reconnection successful');
    } catch (error) {
      console.error('[ChatSignalR Reconnection] ❌ Reconnection failed:', error);
      // The start() method will schedule another reconnection attempt
    }
  }

  /**
   * Register a listener for connection state changes
   */
  onConnectionStateChange(callback: (connected: boolean) => void): () => void {
    this.connectionStateListeners.push(callback);
    
    // Immediately notify with current state
    callback(this.isConnected);
    
    // Return cleanup function
    return () => {
      const index = this.connectionStateListeners.indexOf(callback);
      if (index > -1) {
        this.connectionStateListeners.splice(index, 1);
      }
    };
  }

  /**
   * Notify all listeners of connection state change
   */
  private notifyConnectionStateChange(connected: boolean): void {
    console.log(`[ChatSignalR] Connection state changed: ${connected ? 'CONNECTED' : 'DISCONNECTED'}`);
    this.connectionStateListeners.forEach(listener => {
      try {
        listener(connected);
      } catch (error) {
        console.error('[ChatSignalR] Error in connection state listener:', error);
      }
    });
  }

  /**
   * Join a conversation group to receive updates
   */
  async joinConversation(conversationId: string): Promise<void> {
    if (!this.connection || !this.isConnected) {
      throw new Error('ChatSignalR: Not connected. Call start() first.');
    }

    try {
      await this.connection.invoke('JoinConversation', conversationId);
      console.log(`ChatSignalR: Joined conversation ${conversationId}`);
    } catch (error) {
      console.error(`ChatSignalR: Failed to join conversation ${conversationId}`, error);
      throw error;
    }
  }

  /**
   * Join a website group to receive new conversation notifications
   */
  async joinWebsiteGroup(websiteId: string): Promise<void> {
    if (!this.connection || !this.isConnected) {
      throw new Error('ChatSignalR: Not connected. Call start() first.');
    }

    try {
      await this.connection.invoke('JoinGroup', `website-${websiteId}`);
      console.log(`ChatSignalR: Joined website group website-${websiteId}`);
    } catch (error) {
      console.error(`ChatSignalR: Failed to join website group website-${websiteId}`, error);
      throw error;
    }
  }

  /**
   * Leave a website group
   */
  async leaveWebsiteGroup(websiteId: string): Promise<void> {
    if (!this.connection || !this.isConnected) {
      return; // Already disconnected, nothing to do
    }

    try {
      await this.connection.invoke('LeaveGroup', `website-${websiteId}`);
      console.log(`ChatSignalR: Left website group website-${websiteId}`);
    } catch (error) {
      console.error(`ChatSignalR: Failed to leave website group website-${websiteId}`, error);
    }
  }

  /**
   * Leave a conversation group
   */
  async leaveConversation(conversationId: string): Promise<void> {
    if (!this.connection || !this.isConnected) {
      return; // Already disconnected, nothing to do
    }

    try {
      await this.connection.invoke('LeaveConversation', conversationId);
      console.log(`ChatSignalR: Left conversation ${conversationId}`);
    } catch (error) {
      console.error(`ChatSignalR: Failed to leave conversation ${conversationId}`, error);
    }
  }

  /**
   * Subscribe to incoming messages
   * Enhanced to support affectedParticipants array for unread count updates
   */
  onMessage(callback: SignalRMessageCallback): () => void {
    if (!this.connection) {
      throw new Error('ChatSignalR: Not connected. Call start() first.');
    }

    const handler = (payload: any) => {
      if (!payload) return;

      // Handle both old format (payload = message) and new format (payload.message)
      const messageData = payload.message || payload;

      // Normalize the 'from' field
      let from: ChatMessageDto['from'] = 'agent';
      const pFrom = messageData.from as string | undefined;
      
      if (pFrom === 'system') from = 'system';
      else if (pFrom === 'user') from = 'user';
      else if (pFrom === 'agent') from = 'agent';
      else if (pFrom === 'driver') from = 'driver';

      const message: ChatMessageDto = {
        id: messageData.id || crypto.randomUUID(),
        type: messageData.type || 'text',
        text: messageData.text,
        imageUrl: messageData.imageUrl,
        location: messageData.location ?? undefined,
        from,
        at: messageData.at || new Date().toISOString(),
        conversationId: messageData.conversationId,
        senderSessionId: messageData.senderSessionId,
        fromParticipantId: messageData.fromParticipantId,
        senderUserId: messageData.senderUserId,
        senderName: messageData.senderName,
        senderWebsiteId: messageData.senderWebsiteId,
        senderCompany: messageData.senderCompany
      };

      // Extract affectedParticipants if present (new format)
      const affectedParticipants = payload.affectedParticipants as Array<{ userId: string; newUnreadCount: number }> | undefined;

      callback(message, affectedParticipants);
    };

    this.connection.on('ReceiveMessage', handler);

    // Return cleanup function
    return () => {
      if (this.connection) {
        this.connection.off('ReceiveMessage', handler);
      }
    };
  }

  /**
   * Subscribe to typing indicators
   * Supports both legacy PeerTyping and new UserTyping events
   */
  onTyping(callback: SignalRTypingCallback): () => void {
    if (!this.connection) {
      throw new Error('ChatSignalR: Not connected. Call start() first.');
    }

    const handler = (data: any) => {
      if (data?.conversationId) {
        callback({
          conversationId: data.conversationId,
          userId: data.userId,
          displayName: data.displayName,
          isTyping: !!data.isTyping,
          from: data.from || 'unknown'
        });
      }
    };

    // Subscribe to both old and new event names for compatibility
    this.connection.on('PeerTyping', handler);
    this.connection.on('UserTyping', handler);

    // Return cleanup function
    return () => {
      if (this.connection) {
        this.connection.off('PeerTyping', handler);
        this.connection.off('UserTyping', handler);
      }
    };
  }

  /**
   * Subscribe to read status updates
   * New event for tracking when participants mark messages as read/unread
   */
  onReadStatusUpdated(callback: SignalRReadStatusCallback): () => void {
    if (!this.connection) {
      throw new Error('ChatSignalR: Not connected. Call start() first.');
    }

    const handler = (data: any) => {
      if (data?.conversationId && data?.userId !== undefined) {
        callback({
          conversationId: data.conversationId,
          userId: data.userId,
          role: data.role,
          displayName: data.displayName,
          lastReadAt: data.lastReadAt,
          lastReadMessageId: data.lastReadMessageId,
          unreadCount: data.unreadCount ?? 0
        });
      }
    };

    this.connection.on('ReadStatusUpdated', handler);

    // Return cleanup function
    return () => {
      if (this.connection) {
        this.connection.off('ReadStatusUpdated', handler);
      }
    };
  }

  /**
   * Subscribe to external-delivery outcomes for messages WE sent.
   *
   * Forwarding to Booking.com is fire-and-forget (the operator must not wait on their API to see their own
   * message), so the bubble is rendered optimistically and this event arrives a moment later to say whether
   * it actually reached the traveller. Without it, a failed reply is indistinguishable from a delivered one.
   */
  onMessageDeliveryUpdated(callback: SignalRMessageDeliveryCallback): () => void {
    if (!this.connection) {
      throw new Error('ChatSignalR: Not connected. Call start() first.');
    }

    const handler = (data: any) => {
      if (data?.conversationId && data?.messageId) {
        callback({
          conversationId: data.conversationId,
          messageId: data.messageId,
          channel: data.channel,
          deliveryStatus: data.deliveryStatus
        });
      }
    };

    this.connection.on('MessageDeliveryUpdated', handler);

    return () => {
      if (this.connection) {
        this.connection.off('MessageDeliveryUpdated', handler);
      }
    };
  }

  /**
   * Subscribe to history backfills — messages recovered from Booking.com that our webhook never got.
   * Rare by design, so the client simply refetches rather than trying to splice them into place.
   */
  onHistoryBackfilled(callback: SignalRHistoryBackfilledCallback): () => void {
    if (!this.connection) {
      throw new Error('ChatSignalR: Not connected. Call start() first.');
    }

    const handler = (data: any) => {
      if (data?.conversationId) {
        callback({ conversationId: data.conversationId, recovered: data.recovered ?? 0 });
      }
    };

    this.connection.on('HistoryBackfilled', handler);

    return () => {
      if (this.connection) {
        this.connection.off('HistoryBackfilled', handler);
      }
    };
  }

  /**
   * Subscribe to new conversation notifications (for website groups)
   * Triggered when a customer creates a new conversation
   */
  onNewConversation(callback: SignalRNewConversationCallback): () => void {
    if (!this.connection) {
      throw new Error('ChatSignalR: Not connected. Call start() first.');
    }

    const handler = (data: any) => {
      if (data?.conversationId) {
        callback({
          conversationId: data.conversationId,
          bookingCustomerId: data.bookingCustomerId,
          bookingGuid: data.bookingGuid,
          customerName: data.customerName,
          lastMessage: data.lastMessage,
          websiteId: data.websiteId,
          createdAt: data.createdAt || new Date().toISOString()
        });
      }
    };

    this.connection.on('NewConversation', handler);

    // Return cleanup function
    return () => {
      if (this.connection) {
        this.connection.off('NewConversation', handler);
      }
    };
  }

  /**
   * Subscribe to conversation status changes (resolve / reopen).
   * Broadcast to the website group — received by all agents watching that website.
   */
  onConversationUpdated(callback: SignalRConversationUpdatedCallback): () => void {
    if (!this.connection) {
      throw new Error('ChatSignalR: Not connected. Call start() first.');
    }

    const handler = (data: any) => {
      if (data?.conversationId) {
        callback({
          conversationId: data.conversationId,
          status: data.status === 'resolved' ? 'resolved' : 'open',
          resolvedAt: data.resolvedAt ?? null,
          websiteId: data.websiteId,
          assignedToUserId: data.assignedToUserId ?? null,
          assignedAgentName: data.assignedAgentName ?? null
        });
      }
    };

    this.connection.on('ConversationUpdated', handler);

    return () => {
      if (this.connection) {
        this.connection.off('ConversationUpdated', handler);
      }
    };
  }

  /**
   * Check if connection is active
   */
  isActive(): boolean {
    return this.isConnected && this.connection !== null;
  }
}

// Singleton instance for the entire application
let globalConnection: ChatSignalRConnection | null = null;

/**
 * Get or create the global SignalR connection instance
 */
export function getChatSignalRConnection(): ChatSignalRConnection {
  if (!globalConnection) {
    globalConnection = new ChatSignalRConnection();
  }
  return globalConnection;
}
